// ============================================================================
// FSA CONSOLIDATED PRINT SERVICE
// ============================================================================
// Phase 1 Report Builder — single-call bundle PDF for an engagement.
// Auditor picks which statements + notes to include via the Print
// Financial Statements modal; this service posts the selection to the
// backend and returns the PDF Blob for inline iframe preview.
//
// Endpoint: POST /api/v1/FsaConsolidatedPrint/by-engagement/{engId}/preview-pdf
// Body:    { reportIds: ID[], noteScheduleIds: ID[], mode: 'actual' | 'rounded' }
// Returns: application/pdf binary
//
// Order policy: backend renders statements first (page-break between
// each), then notes flowing continuously with the repeating "Notes to …
// (Contd.)" banner. Frontend sends statements in template display_order
// and notes in saved note_number order — backend trusts that ordering.

import { post } from '../../client/axiosClient';
import { buildEndpointWithParams } from '../../endpoints/base.endpoints';
import type { ID } from '../../types';

export interface ConsolidatedPrintRequest {
  reportIds: ID[];
  noteScheduleIds: ID[];
  mode: 'actual' | 'rounded';
}

/** ADR-0024 — one document of the Annual Report pack, in pack order. */
export interface AnnualReportPartRequest {
  kind: 'FINANCIAL_STATEMENTS' | 'REPORT' | 'AGM_NOTICE' | 'AGM_PROXY' | 'AGM_ATTENDANCE';
  title: string;
  reportId?: ID;
  reportIds?: ID[];
  noteScheduleIds?: ID[];
}

export interface AnnualReportRequest {
  includeCover: boolean;
  includeContents: boolean;
  mode: 'actual' | 'rounded';
  parts: AnnualReportPartRequest[];
}

class FsaConsolidatedPrintService {
  /** ADR-0024 — the Annual Report pack (cover, Contents, documents joined in the order sent). */
  async getAnnualReportPdfBlob(engagementId: ID, request: AnnualReportRequest): Promise<Blob> {
    const url = buildEndpointWithParams(
      '/FsaConsolidatedPrint/by-engagement/:engagementId/annual-report-pdf',
      { engagementId },
    );
    const response = await post<Blob>(url, request, { responseType: 'blob' });
    return response.data;
  }

  async getPreviewPdfBlob(
    engagementId: ID,
    request: ConsolidatedPrintRequest,
  ): Promise<Blob> {
    // buildEndpointWithParams prepends `/v1` via apiConfig.apiVersion;
    // a bare path like `/FsaConsolidatedPrint/...` would resolve to
    // /api/FsaConsolidatedPrint/... (missing the /v1) and 404.
    const url = buildEndpointWithParams(
      '/FsaConsolidatedPrint/by-engagement/:engagementId/preview-pdf',
      { engagementId },
    );
    const response = await post<Blob>(
      url,
      request,
      { responseType: 'blob' },
    );
    return response.data;
  }
}

export const fsaConsolidatedPrintService = new FsaConsolidatedPrintService();
export default fsaConsolidatedPrintService;
